import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Button, IconButton, Tooltip, Select, MenuItem, LinearProgress, Dialog, DialogTitle, DialogContent, TextField, DialogActions, Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import PDFModalExit from '../components/PDFModalExit';
import { fetchCurrentUser } from '../services/auth';

const ExitAuthorizationsPage = () => {
  const [exitAuthorizations, setExitAuthorizations] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [selectedExitAuth, setSelectedExitAuth] = useState(null);
  const [openPDFModal, setOpenPDFModal] = useState(false);
  const [theUser, setCurrentUser] = useState(null); // 1 for admin, other values for non-admin
  const [loading, setLoading] = useState(true);
  const [openRefuseModal, setOpenRefuseModal] = useState(false);
  const [refuseReason, setRefuseReason] = useState('');
  const [selectedIdToRefuse, setSelectedIdToRefuse] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editingExit, setEditingExit] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [editForm, setEditForm] = useState({
    employee_id: '',
    reason: '',
    sortie_date: '',
    sortie_time: '',
    entree_date: '',
    entree_time: ''
  });
  const navigate = useNavigate();
  // Date range filter state
  const [dateFrom, setDateFrom] = useState(''); // yyyy-mm-dd
  const [dateTo, setDateTo] = useState('');     // yyyy-mm-dd
  const [dateFilterMode, setDateFilterMode] = useState('any'); // 'started' | 'finished' | 'any'

  const filterOptions = [
    { value: 'all', label: 'Tous' },
    { value: 'validated', label: 'Validés' },
    { value: 'non-validated', label: 'Non Validés' },
    { value: 'refused', label: 'Refusés' },
  ];

  const filteredExitAuthorizations = exitAuthorizations
    .filter((ea) => {
      if (filterStatus === 'validated') return ea.validated;
      if (filterStatus === 'non-validated') return !ea.validated && !ea.refused;
      if (filterStatus === 'refused') return ea.refused;
      return true;
    })
    .filter((ea) => {
      if (!dateFrom && !dateTo) return true;
      const startRange = dateFrom ? new Date(dateFrom + 'T00:00:00').getTime() : -Infinity;
      const endRange = dateTo ? new Date(dateTo + 'T23:59:59').getTime() : Infinity;
      const startedInRange = ea.sortieTs >= startRange && ea.sortieTs <= endRange;
      const finishedInRange = ea.entreeTs >= startRange && ea.entreeTs <= endRange;
      if (dateFilterMode === 'started') return startedInRange;
      if (dateFilterMode === 'finished') return finishedInRange;
      return startedInRange || finishedInRange;
    })
    .filter((ea) => {
      if (!filterEmployee) return true;
      const employeeName = ea.employee ? `${ea.employee.first_name} ${ea.employee.last_name}` : '';
      return employeeName.toLowerCase().includes(filterEmployee.toLowerCase());
    });

  useEffect(() => {
    const loadUser = async () => {
      const currentUser = await fetchCurrentUser();
      if (currentUser) {
        setCurrentUser(currentUser);
      }
      setLoading(false);
    };
    loadUser();
  }, []);

  useEffect(() => {
    const fetchExitAuthorizations = async () => {
      try {
        const response = await api.get('/exit-authorizations');
        const sortedData = response.data.sort((a, b) => b.id - a.id);
        const formattedData = sortedData.map(ea => {
          const parseTs = (dStr, tStr) => {
            const d = new Date(dStr);
            if (tStr) {
              const [hh = '0', mm = '0'] = String(tStr).split(':');
              d.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);
            }
            return d.getTime();
          };
          const sortieTs = parseTs(ea.sortie_date, ea.sortie_time);
          const entreeTs = parseTs(ea.entree_date, ea.entree_time);
          return {
            ...ea,
            employeeName: ea.employee ? `${ea.employee.first_name} ${ea.employee.last_name}` : '............................',
            sortieDateTime: `${new Date(ea.sortie_date).toLocaleDateString()} ${ea.sortie_time}`,
            entreeDateTime: `${new Date(ea.entree_date).toLocaleDateString()} ${ea.entree_time}`,
            sortieTs,
            entreeTs,
          };
        });
        setExitAuthorizations(formattedData);
      } catch (error) {
        console.error('Error loading exit authorizations:', error);
      }
    };
    fetchExitAuthorizations();
  }, []);

  const handleExportCSV = () => {
    const rows = filteredExitAuthorizations;
    if (!rows || rows.length === 0) {
      alert('Aucune donnée à exporter.');
      return;
    }
    const header = [
      'Employé',
      'Motif',
      'Date et Heure de Sortie',
      'Date et Heure d’Entrée',
      'Motif de Refus',
      'Validé',
      'Refusé'
    ];
    const escapeCsv = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      if (s.includes('"') || s.includes(',') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };
    const csv = [header.join(',')]
      .concat(
        rows.map((r) =>
          [
            r.employeeName,
            r.reason || '',
            r.sortieDateTime,
            r.entreeDateTime,
            r.refuseReason || '',
            r.validated ? 'oui' : 'non',
            r.refused ? 'oui' : 'non',
          ].map(escapeCsv).join(',')
        )
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const now = new Date();
    const ts = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    link.download = `autorisations_sortie_${ts}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this exit authorization?')) {
      try {
        await api.delete(`/exit-authorizations/${id}`);
        setExitAuthorizations(prev => prev.filter(ea => ea.id !== id));
      } catch (error) {
        console.error('Error deleting exit authorization:', error);
      }
    }
  };

  const handleRowClick = async (params) => {
    try {
      const response = await api.get(`/exit-authorizations/${params.row.id}`);
      const employee = await api.get(`/employees/${response.data.employee_id}`);
      response.data.employee = employee.data;
      setSelectedDetails(response.data);
      setDetailsOpen(true);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
    }
  };

  const handleViewPDF = async (authId) => {
    try {
      const response = await api.get(`/exit-authorizations/${authId}`);
      const employee = await api.get(`/employees/${response.data.employee_id}`);
      response.data.employee = employee.data;
      setSelectedExitAuth(response.data);
      setOpenPDFModal(true);
    } catch (error) {
      console.error('Erreur lors du chargement du PDF:', error);
    }
  };

  const onEditOpen = (exit) => {
    setEditingExit(exit);
    setEditForm({
      employee_id: exit.employee_id || '',
      reason: exit.reason || '',
      sortie_date: exit.sortie_date ? new Date(exit.sortie_date).toISOString().split('T')[0] : '',
      sortie_time: exit.sortie_time || '',
      entree_date: exit.entree_date ? new Date(exit.entree_date).toISOString().split('T')[0] : '',
      entree_time: exit.entree_time || ''
    });
    setEditOpen(true);
  };

  const onEditClose = () => {
    setEditOpen(false);
    setEditingExit(null);
    setEditForm({
      employee_id: '',
      reason: '',
      sortie_date: '',
      sortie_time: '',
      entree_date: '',
      entree_time: ''
    });
  };

  const onEditChange = (e) => setEditForm({ ...editForm, [e.target.name]: e.target.value });

  const onEditSubmit = async () => {
    if (!editingExit) return;
    try {
      await api.put(`/exit-authorizations/${editingExit.id}`, editForm);
      onEditClose();
      // Reload data
      const response = await api.get('/exit-authorizations');
      const formattedData = response.data.map((ea) => {
        const sortieTs = new Date(ea.sortie_date + ' ' + ea.sortie_time).getTime();
        const entreeTs = new Date(ea.entree_date + ' ' + ea.entree_time).getTime();
        return {
          ...ea,
          employeeName: ea.employee ? `${ea.employee.first_name} ${ea.employee.last_name}` : '............................',
          sortieDateTime: `${new Date(ea.sortie_date).toLocaleDateString()} ${ea.sortie_time}`,
          entreeDateTime: `${new Date(ea.entree_date).toLocaleDateString()} ${ea.entree_time}`,
          sortieTs,
          entreeTs,
        };
      });
      setExitAuthorizations(formattedData);
    } catch (error) {
      console.error('Error updating exit authorization:', error);
      alert('Erreur lors de la modification de la sortie');
    }
  };

  const handleRefuse = async () => {
    if (!refuseReason.trim()) {
      alert('Please provide a refusal reason.');
      return;
    }

    try {
      await api.post('/exit-authorizations/refuse', { id: selectedIdToRefuse, refuseReason });
      setExitAuthorizations(prev =>
        prev.map(ea =>
          ea.id === selectedIdToRefuse ? { ...ea, refused: true, refuseReason } : ea
        )
      );
      setOpenRefuseModal(false);
      setRefuseReason('');
    } catch (error) {
      console.error('Error refusing exit authorization:', error);
    }
  };

  const columns = [
    { field: 'employeeName', headerName: 'Employé', width: 200, headerClassName: 'super-app-theme--header' },
    { field: 'reason', headerName: 'Motif', flex: 1, minWidth: 200, headerClassName: 'super-app-theme--header' },
    { field: 'sortieDateTime', headerName: 'Date et Heure de Sortie', width: 180, headerClassName: 'super-app-theme--header' },
    { field: 'entreeDateTime', headerName: 'Date et Heure d\'Entrée', width: 180, headerClassName: 'super-app-theme--header' },
    { field: 'refuseReason', headerName: 'Motif de Refus', width: 180, headerClassName: 'super-app-theme--header' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 280,
      sortable: false,
      filterable: false,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const userRole = theUser?.role.toString(); // Example admin role (1 = admin, 5 = admin-tenes)

        return (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', py: 1 }}>
            {!params.row.validated && !params.row.refused && (userRole === "1" || userRole === "5" || theUser?.role === "admin-tenes") && (
              <>
                <Tooltip title="Valider">
                  <IconButton
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation(); // Empêche la propagation du clic
                      try {
                        await api.post('/exit-authorizations/validate', { id: params.row.id });
                        setExitAuthorizations(prev =>
                          prev.map(ea =>
                            ea.id === params.row.id ? { ...ea, validated: true } : ea
                          )
                        );
                      } catch (error) {
                        console.error('Erreur lors de la validation:', error);
                      }
                    }}
                  >
                    <CheckCircleIcon sx={{ color: 'green' }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Refuser">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // Empêche la propagation du clic
                      setSelectedIdToRefuse(params.row.id);
                      setOpenRefuseModal(true);
                    }}
                  >
                    <CancelIcon sx={{ color: 'red' }} />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {params.row.validated && (
              <Tooltip title="Voir PDF">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  handleViewPDF(params.row.id);
                }}>
                  <PictureAsPdfIcon sx={{ color: 'blue' }} />
                </IconButton>
              </Tooltip>
            )}

            {(userRole === "1" || userRole === "5" || theUser?.role === "admin-tenes") && (
              <Tooltip title="Supprimer">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  handleDelete(params.row.id);
                }}>
                  <DeleteIcon sx={{ color: 'red' }} />
                </IconButton>
              </Tooltip>
            )}
            {(userRole === "1" || userRole === "5" || theUser?.role === "admin-tenes") && (
              <Tooltip title="Modifier">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  onEditOpen(params.row);
                }}>
                  <EditIcon sx={{ color: 'blue' }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <>
      <h2>Autorisations de Sortie</h2>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/create-exit-auth')}
          style={{ marginRight: 20 }}
        >
          Créer une nouvelle autorisation de sortie
        </Button>
        <Select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          variant="outlined"
          style={{ minWidth: 150 }}
        >
          {filterOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <TextField
          label="Employé"
          value={filterEmployee}
          onChange={(e) => setFilterEmployee(e.target.value)}
          placeholder="Filtrer par employé..."
          variant="outlined"
          style={{ minWidth: 200 }}
        />
        <TextField
          label="De"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="Jusqu'à"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Select
          value={dateFilterMode}
          onChange={(e) => setDateFilterMode(e.target.value)}
          size="small"
          style={{ minWidth: 170 }}
        >
          <MenuItem value="any">Sortie OU entrée dans l'intervalle</MenuItem>
          <MenuItem value="started">Sortie dans l'intervalle</MenuItem>
          <MenuItem value="finished">Entrée dans l'intervalle</MenuItem>
        </Select>
        <Button variant="outlined" onClick={handleExportCSV}>Exporter CSV</Button>
      </div>
      <div style={{ height: 600, width: '100%', marginTop: 10 }}>
        <DataGrid
          rows={filteredExitAuthorizations}
          columns={columns}
          pageSize={10}
          autoPageSize={false}
          disableSelectionOnClick
          getRowHeight={() => 80}
          onRowClick={handleRowClick}
          getRowClassName={(params) =>
            params.row.validated ? 'validated-row' :
            params.row.refused ? 'refused-row' : 'non-validated-row'
          }
          sx={{
            '& .super-app-theme--header': {
              backgroundColor: '#1976d2',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
            },
            '& .MuiDataGrid-root': {
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#1976d2',
              fontSize: '14px',
              fontWeight: 600,
              color: 'white',
              borderBottom: '2px solid #1565c0',
            },
            '& .MuiDataGrid-columnHeader': {
              '&:focus': {
                outline: 'none',
              },
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            },
            '& .MuiDataGrid-cell': {
              fontSize: '13px',
              padding: '16px 8px',
              borderBottom: '1px solid #f0f0f0',
              '&:focus': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-row': {
              '&:nth-of-type(even)': {
                backgroundColor: '#fafafa',
              },
              '&:hover': {
                backgroundColor: '#e3f2fd',
                cursor: 'pointer'
              },
              '&.Mui-selected': {
                backgroundColor: '#e3f2fd',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#f5f5f5',
            },
            '& .MuiDataGrid-pagination': {
              '& .MuiIconButton-root': {
                borderRadius: '4px',
              },
            },
            '.validated-row': { backgroundColor: '#e8f5e8' },
            '.non-validated-row': { backgroundColor: '#fff8e1' },
            '.refused-row': { backgroundColor: '#ffebee' },
          }}
        />
      </div>

      {openPDFModal && (
        <PDFModalExit
          missionData={selectedExitAuth}
          onClose={() => setOpenPDFModal(false)}
        />
      )}

      <Dialog open={openRefuseModal} onClose={() => setOpenRefuseModal(false)}>
        <DialogTitle>Refuser Autorisation de Sortie</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Motif de Refus"
            fullWidth
            multiline
            rows={3}
            value={refuseReason}
            onChange={(e) => setRefuseReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRefuseModal(false)}>Annuler</Button>
          <Button onClick={handleRefuse} color="error">Refuser</Button>
        </DialogActions>
      </Dialog>

      {/* Details Modal */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Détails de l'autorisation de sortie</DialogTitle>
        <DialogContent>
          {selectedDetails && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '10px' }}>
              <div><strong>Employé:</strong> {selectedDetails.employee?.first_name} {selectedDetails.employee?.last_name}</div>
              <div><strong>Motif:</strong> {selectedDetails.reason}</div>
              <div><strong>Date de sortie:</strong> {new Date(selectedDetails.sortie_date).toLocaleDateString()}</div>
              <div><strong>Heure de sortie:</strong> {selectedDetails.sortie_time}</div>
              <div><strong>Date d'entrée:</strong> {new Date(selectedDetails.entree_date).toLocaleDateString()}</div>
              <div><strong>Heure d'entrée:</strong> {selectedDetails.entree_time}</div>
              <div><strong>Statut:</strong> 
                {selectedDetails.validated ? 
                  <span style={{ color: 'green', marginLeft: '8px' }}>✓ Validé</span> : 
                  selectedDetails.refused ? 
                  <span style={{ color: 'red', marginLeft: '8px' }}>✗ Refusé</span> :
                  <span style={{ color: 'orange', marginLeft: '8px' }}>⏳ En attente</span>
                }
              </div>
              {selectedDetails.refuseReason && (
                <div><strong>Motif de refus:</strong> {selectedDetails.refuseReason}</div>
              )}
              <div><strong>Créé le:</strong> {new Date(selectedDetails.createdAt).toLocaleDateString()} à {new Date(selectedDetails.createdAt).toLocaleTimeString()}</div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Exit Authorization Dialog */}
      <Dialog open={editOpen} onClose={onEditClose} maxWidth="md" fullWidth>
        <DialogTitle>Modifier l'autorisation de sortie</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Motif"
            name="reason"
            value={editForm.reason}
            onChange={onEditChange}
            fullWidth
            multiline
            rows={3}
          />
          <TextField
            margin="dense"
            label="Date de sortie"
            type="date"
            name="sortie_date"
            value={editForm.sortie_date}
            onChange={onEditChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="Heure de sortie"
            type="time"
            name="sortie_time"
            value={editForm.sortie_time}
            onChange={onEditChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="Date d'entrée"
            type="date"
            name="entree_date"
            value={editForm.entree_date}
            onChange={onEditChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="Heure d'entrée"
            type="time"
            name="entree_time"
            value={editForm.entree_time}
            onChange={onEditChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onEditClose}>Annuler</Button>
          <Button onClick={onEditSubmit} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExitAuthorizationsPage;